import { useState, useEffect } from 'react';
import type { Candidate, ValidationErrors } from '../types/Candidate';
import { US_STATES, DEVICES, HOURS_OPTIONS, YES_NO_OPTIONS } from '../constants/formOptions';
import { validateForm } from '../utils/candidateValidation';
import './EmploymentApplication-base.css';
import './EmploymentApplication-forms.css';
import './EmploymentApplication-buttons.css';
import './EmploymentApplication-alerts.css';
import './EmploymentApplication-utilities.css';
import './EmploymentApplication-responsive.css';

declare global {
  interface Window {
    grecaptcha: {
      render: (container: string, params: { sitekey: string; callback: (response: string) => void }) => void;
      reset: () => void;
    };
    loadCaptcha?: () => void;
  }
}

const IS_DEV = import.meta.env.DEV;

function EmploymentApplication() {
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [message, setMessage] = useState<{ type: 'success' | 'danger' | 'info'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');

  useEffect(() => {
    if (IS_DEV) {
      setRecaptchaToken('ok');
    } else {
      loadRecaptcha();
    }
  }, []);

  const loadRecaptcha = () => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=loadCaptcha&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    window.loadCaptcha = () => {
      window.grecaptcha.render('captcha_container', {
        sitekey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
        callback: (response: string) => {
          setRecaptchaToken(response);
        }
      });
    };
  };

  const handleDeviceChange = (deviceId: string, checked: boolean) => {
    setSelectedDevices(prev =>
      checked ? [...prev, deviceId] : prev.filter(d => d !== deviceId)
    );
  };

  const autofillForm = () => {
    (document.getElementById('firstName') as HTMLInputElement).value = 'Doug';
    (document.getElementById('lastName') as HTMLInputElement).value = 'Vogel';
    (document.getElementById('email') as HTMLInputElement).value = 'test@retailassistance.com';
    (document.getElementById('address') as HTMLInputElement).value = '2173 E Chesapeake';
    (document.getElementById('city') as HTMLInputElement).value = 'Gilbert';
    (document.getElementById('state') as HTMLSelectElement).value = 'AZ';
    (document.getElementById('zip') as HTMLInputElement).value = '85234';
    (document.getElementById('cellPhone') as HTMLInputElement).value = '480-258-0475';
    (document.getElementById('hoursAvailablePerMonth') as HTMLSelectElement).value = '31_to_40';
    (document.getElementById('howHeardAboutRac') as HTMLInputElement).value = 'from a friend';
    (document.getElementById('hasDemonstratingExperience') as HTMLSelectElement).value = 'yes';
    (document.getElementById('canReadPlanogram') as HTMLSelectElement).value = 'yes';
    (document.getElementById('currentCities') as HTMLTextAreaElement).value = 'Tempe, Phoenix';
    (document.getElementById('currentMalls') as HTMLTextAreaElement).value = 'Fiesta, Fashion';
    (document.getElementById('pastOrCurrentStores') as HTMLTextAreaElement).value = 'Macys, dillars, jcp';
    (document.getElementById('pastOrCurrentProducts') as HTMLTextAreaElement).value = 'tommy, billy, phil';
    (document.getElementById('hasPrinter') as HTMLSelectElement).value = 'yes';
    (document.getElementById('hasAbilityToUploadPhotos') as HTMLSelectElement).value = 'yes';
    (document.getElementById('hasReliableTransportation') as HTMLSelectElement).value = 'yes';
    (document.getElementById('otherFirms') as HTMLTextAreaElement).value = 'None';
    setSelectedDevices(['iPhone', 'macbook']);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const validationErrors = validateForm(formData, selectedDevices);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setMessage({ type: 'danger', text: 'Please correct the errors in the form' });
      if (!IS_DEV && window.grecaptcha) {
        window.grecaptcha.reset();
        setRecaptchaToken('');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!recaptchaToken && !IS_DEV) {
      setMessage({ type: 'danger', text: 'Please complete the reCAPTCHA' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setMessage(null);

    try {
      const candidate: Candidate = {
        firstName: formData.get('firstName') as string,
        lastName: formData.get('lastName') as string,
        email: formData.get('email') as string,
        address: formData.get('address') as string,
        city: formData.get('city') as string,
        state: formData.get('state') as string,
        zip: formData.get('zip') as string,
        cellPhone: formData.get('cellPhone') as string,
        homePhone: formData.get('homePhone') as string || '',
        hoursAvailablePerMonth: formData.get('hoursAvailablePerMonth') as string,
        howHeardAboutRac: formData.get('howHeardAboutRac') as string,
        hasDemonstratingExperience: formData.get('hasDemonstratingExperience') as string,
        currentCities: formData.get('currentCities') as string,
        currentMalls: formData.get('currentMalls') as string,
        devicesOnInternet: selectedDevices.join(', '),
        hasPrinter: formData.get('hasPrinter') as string,
        abilityToUploadPhotos: formData.get('hasAbilityToUploadPhotos') as string,
        hasReliableTransportation: formData.get('hasReliableTransportation') as string,
        internetSpeed: '',
        canReadPlanogram: formData.get('canReadPlanogram') as string,
        pastOrCurrentStores: formData.get('pastOrCurrentStores') as string,
        pastOrCurrentProducts: formData.get('pastOrCurrentProducts') as string,
        otherFirms: formData.get('otherFirms') as string,
        recaptchaResponse: recaptchaToken
      };

      const response = await fetch('/api/application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(candidate)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to submit application');
      }

      setMessage({
        type: 'success',
        text: 'Your application has been submitted successfully! We will contact you if there is an opening.'
      });
      
      form.reset();
      setSelectedDevices([]);
      if (!IS_DEV && window.grecaptcha) {
        window.grecaptcha.reset();
      }
      
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error instanceof Error ? error.message : 'An error occurred while submitting your application'
      });
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="contactContainer">
          <div className="logo-container">
            <img src="/images/2021-logo.jpg" alt="Retail Assistance Corporation Logo" className="company-logo" />
          </div>

          <div className="card border-primary">
            <div className="card-header bg-primary text-white">Retail Assistance Corporation - Employment Application</div>
            <div className="card-body">
              <p>We are currently hiring merchandisers and demonstrators.</p>
              <p>Please fill in the form below. If there is an opening we will e-mail or telephone you.</p>
            </div>
          </div>

          {IS_DEV && (
            <div className="card border-danger">
              <div className="card-header bg-danger text-white">DEV ONLY</div>
              <div className="card-body">
                <button className="btn btn-danger" type="button" onClick={autofillForm}>Dev Autofill</button>
              </div>
              <br /><br />
            </div>
          )}

          {message && (
            <div className={`alert alert-${message.type === 'success' ? 'success' : message.type === 'danger' ? 'danger' : 'info'}`} role="alert">
              {message.type === 'success' && <span aria-hidden="true">✓ </span>}
              {message.type === 'danger' && <span aria-hidden="true">⚠ </span>}
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="contactForm" noValidate>
            <h3>Your Contact Information</h3>
            <div className="formGroup">
                <label htmlFor="firstName"><span className="text-danger">*</span> First Name</label>
                <input type="text" className={`form-control ${errors.firstName ? 'is-invalid' : ''}`} id="firstName" name="firstName" maxLength={50} />
                {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
            </div>

            <div className="formGroup">
                <label htmlFor="lastName"><span className="text-danger">*</span> Last Name</label>
                <input type="text" className={`form-control ${errors.lastName ? 'is-invalid' : ''}`} id="lastName" name="lastName" maxLength={50} />
                {errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
            </div>

              <div className="formGroup">
                  <label htmlFor="email"><span className="text-danger">*</span> Email</label>
                  <input type="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} id="email" name="email" maxLength={100} />
                  {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="address"><span className="text-danger">*</span> Address</label>
                  <input type="text" className={`form-control ${errors.address ? 'is-invalid' : ''}`} id="address" name="address" maxLength={100} />
                  {errors.address && <div className="invalid-feedback d-block">{errors.address}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="city"><span className="text-danger">*</span> City</label>
                  <input type="text" className={`form-control ${errors.city ? 'is-invalid' : ''}`} id="city" name="city" maxLength={50} />
                  {errors.city && <div className="invalid-feedback d-block">{errors.city}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="state"><span className="text-danger">*</span> State</label>
                  <select className={`form-control ${errors.state ? 'is-invalid' : ''}`} id="state" name="state">
                    <option value=""></option>
                    {US_STATES.map(state => (
                      <option key={state.value} value={state.value}>{state.label}</option>
                    ))}
                  </select>
                  {errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="zip"><span className="text-danger">*</span> Zip Code</label>
                  <input type="text" className={`form-control ${errors.zip ? 'is-invalid' : ''}`} id="zip" name="zip" maxLength={5} />
                  {errors.zip && <div className="invalid-feedback d-block">{errors.zip}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="cellPhone"><span className="text-danger">*</span> Cell Phone</label>
                  <input type="text" className={`form-control ${errors.cellPhone ? 'is-invalid' : ''}`} id="cellPhone" name="cellPhone" placeholder="###-###-####" maxLength={12} />
                  {errors.cellPhone && <div className="invalid-feedback d-block">{errors.cellPhone}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="homePhone">Home Phone</label>
                  <input type="text" className={`form-control ${errors.homePhone ? 'is-invalid' : ''}`} id="homePhone" name="homePhone" placeholder="###-###-####" maxLength={12} />
                  {errors.homePhone && <div className="invalid-feedback d-block">{errors.homePhone}</div>}
              </div>

            <br />
            <h3>Your Merchandising Experience</h3>

            <div className="formGroup">
                <label htmlFor="hasDemonstratingExperience"><span className="text-danger">*</span> Do you have Demonstrating experience?</label>
                <select className={`form-control ${errors.hasDemonstratingExperience ? 'is-invalid' : ''}`} id="hasDemonstratingExperience" name="hasDemonstratingExperience">
                  <option value=""></option>
                  {YES_NO_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {errors.hasDemonstratingExperience && <div className="invalid-feedback d-block">{errors.hasDemonstratingExperience}</div>}
            </div>

              <div className="formGroup">
                  <label htmlFor="canReadPlanogram"><span className="text-danger">*</span> Can you read a Planogram?</label>
                  <select className={`form-control ${errors.canReadPlanogram ? 'is-invalid' : ''}`} id="canReadPlanogram" name="canReadPlanogram">
                    <option value=""></option>
                    {YES_NO_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.canReadPlanogram && <div className="invalid-feedback d-block">{errors.canReadPlanogram}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="otherFirms"><span className="text-danger">*</span> Please list any other Merchandising Firms that you currently work for:</label>
                  <textarea className={`form-control ${errors.otherFirms ? 'is-invalid' : ''}`} id="otherFirms" name="otherFirms" rows={3}></textarea>
                  {errors.otherFirms && <div className="invalid-feedback d-block">{errors.otherFirms}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="currentCities"><span className="text-danger">*</span> Please list the Cities you currently do merchandising work in:</label>
                  <textarea className={`form-control ${errors.currentCities ? 'is-invalid' : ''}`} id="currentCities" name="currentCities" rows={3}></textarea>
                  {errors.currentCities && <div className="invalid-feedback d-block">{errors.currentCities}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="currentMalls"><span className="text-danger">*</span> Please list the Malls you currently do merchandising work in:</label>
                  <textarea className={`form-control ${errors.currentMalls ? 'is-invalid' : ''}`} id="currentMalls" name="currentMalls" rows={3}></textarea>
                  {errors.currentMalls && <div className="invalid-feedback d-block">{errors.currentMalls}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="pastOrCurrentStores"><span className="text-danger">*</span> Please list the Stores where you currently do merchandising work, or have worked in the past:</label>
                  <textarea className={`form-control ${errors.pastOrCurrentStores ? 'is-invalid' : ''}`} id="pastOrCurrentStores" name="pastOrCurrentStores" rows={3}></textarea>
                  {errors.pastOrCurrentStores && <div className="invalid-feedback d-block">{errors.pastOrCurrentStores}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="pastOrCurrentProducts"><span className="text-danger">*</span> Please list the Products that you currently service, or have serviced in the past:</label>
                  <textarea className={`form-control ${errors.pastOrCurrentProducts ? 'is-invalid' : ''}`} id="pastOrCurrentProducts" name="pastOrCurrentProducts" rows={3}></textarea>
                  {errors.pastOrCurrentProducts && <div className="invalid-feedback d-block">{errors.pastOrCurrentProducts}</div>}
              </div>

                  <br />
            <h3>Your Technology</h3>

            <div className="formGroup">
                <label><span className="text-danger">*</span> Please check ALL devices you own that have internet connectivity</label>
                <div>
                  {DEVICES.map(device => (
                    <div key={device.value} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={device.value}
                        checked={selectedDevices.includes(device.value)}
                        onChange={(e) => handleDeviceChange(device.value, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={device.value}>{device.label}</label>
                    </div>
                  ))}
                </div>
                {errors.devices && <div className="invalid-feedback d-block">{errors.devices}</div>}
            </div>

              <div className="formGroup">
                  <label htmlFor="hasPrinter"><span className="text-danger">*</span> Do you have working printer?</label>
                  <select className={`form-control ${errors.hasPrinter ? 'is-invalid' : ''}`} id="hasPrinter" name="hasPrinter">
                    <option value=""></option>
                    {YES_NO_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.hasPrinter && <div className="invalid-feedback d-block">{errors.hasPrinter}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="hasAbilityToUploadPhotos"><span className="text-danger">*</span> Do you have the ability to upload photos to a website?</label>
                  <select className={`form-control ${errors.hasAbilityToUploadPhotos ? 'is-invalid' : ''}`} id="hasAbilityToUploadPhotos" name="hasAbilityToUploadPhotos">
                    <option value=""></option>
                    {YES_NO_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.hasAbilityToUploadPhotos && <div className="invalid-feedback d-block">{errors.hasAbilityToUploadPhotos}</div>}
              </div>

                  <br />
            <h3>Other</h3>

            <div className="formGroup">
                <label htmlFor="hoursAvailablePerMonth"><span className="text-danger">*</span> How many hours are you available to work per month?</label>
                <select className={`form-control ${errors.hoursAvailablePerMonth ? 'is-invalid' : ''}`} id="hoursAvailablePerMonth" name="hoursAvailablePerMonth">
                  <option value=""></option>
                  {HOURS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {errors.hoursAvailablePerMonth && <div className="invalid-feedback d-block">{errors.hoursAvailablePerMonth}</div>}
            </div>

              <div className="formGroup">
                  <label htmlFor="hasReliableTransportation"><span className="text-danger">*</span> Do you have reliable transportation?</label>
                  <select className={`form-control ${errors.hasReliableTransportation ? 'is-invalid' : ''}`} id="hasReliableTransportation" name="hasReliableTransportation">
                    <option value=""></option>
                    {YES_NO_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.hasReliableTransportation && <div className="invalid-feedback d-block">{errors.hasReliableTransportation}</div>}
              </div>

              <div className="formGroup">
                  <label htmlFor="howHeardAboutRac"><span className="text-danger">*</span> How did you hear about RAC?</label>
                  <input type="text" className={`form-control ${errors.howHeardAboutRac ? 'is-invalid' : ''}`} id="howHeardAboutRac" name="howHeardAboutRac" maxLength={100} />
                  {errors.howHeardAboutRac && <div className="invalid-feedback d-block">{errors.howHeardAboutRac}</div>}
              </div>

            <>
              {!IS_DEV && (
                <div className="formGroup">
                    <br /><br />
                    <div id='captcha_container'></div>
                </div>
              )}

              <br /><br />
              <div className="formGroup">
                  <button className="btn btn-success" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        Submitting...
                        <span className="spinner"></span>
                      </>
                    ) : (
                      'Submit Employment Application'
                    )}
                  </button>
              </div>
            </>
          </form>
        </div>
    </>
  );
}

export default EmploymentApplication;
